import React from 'react';
import { Link } from 'react-router-dom';
import { toUploadUrl } from '../config/api';
import { buildPackageUrl } from '../utils/slugs';

function formatCategoryLabel(value) {
  const normalized = String(value || 'general').toLowerCase();
  const labels = {
    group: 'Group',
    corporate: 'Corporate',
    family: 'Family',
    honeymoon: 'Honeymoon',
    adventure: 'Adventure',
    luxury: 'Luxury',
    budget: 'Budget',
    nature: 'Nature',
    spiritual: 'Spiritual'
  };

  return labels[normalized] || 'General';
}

const PackageCard = ({ package: pkg }) => {
  const getImageUrl = (imagePath) => {
    return toUploadUrl(imagePath, {
      fallback: 'https://placehold.co/800x600/1a1a1a/ffffff?text=No+Image'
    });
  };

  if (!pkg) return null;

  const displayRating = Number(pkg.average_rating || pkg.rating || 0) > 0
    ? Number(pkg.average_rating || pkg.rating).toFixed(1)
    : '4.5';
  const displayReviews = Number(pkg.review_count || pkg.reviews || 0);

  // Calculate discount percentage if original price exists
  const discountPercentage = pkg.originalPrice && pkg.price 
    ? Math.round(((pkg.originalPrice - pkg.price) / pkg.originalPrice) * 100) 
    : null;

  return (
    <div className="package-card">
      {/* Card Badges */}
      <div className="card-badges">
        {pkg.discount && (
          <div className="badge discount-badge">
            <span className="badge-text">{pkg.discount} OFF</span>
          </div>
        )}
        {pkg.package_category && String(pkg.package_category).toLowerCase() !== 'general' && (
          <div className={`badge category-badge ${String(pkg.package_category).toLowerCase()}`}>
            <span className="badge-text">{formatCategoryLabel(pkg.package_category)}</span>
          </div>
        )}
        {discountPercentage && !pkg.discount && discountPercentage > 0 && (
          <div className="badge discount-badge">
            <span className="badge-text">{discountPercentage}% OFF</span>
          </div>
        )}
      
      </div>

      {/* Image Section */}
      <div className="card-image-wrapper">
        <div 
          className="card-image" 
          style={{ backgroundImage: `url(${getImageUrl(pkg.image)})` }}
        >
          <div className="image-overlay"></div>
          <div className="image-content">
            <span className="duration-badge">
              <i className="far fa-clock"></i>
              {pkg.duration || 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="card-content">
        {/* Title */}
        <h3 className="package-title">{pkg.title || 'Package Name'}</h3>
        
        {/* Destinations */}
        <div className="destinations">
          <i className="fas fa-map-marker-alt"></i>
          <span>{pkg.destinations || 'Destination not specified'}</span>
        </div>

        <div className="rating-section">
          <div className="stars" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((index) => (
              <i
                key={index}
                className="fas fa-star"
                style={{ color: index < Math.round(Number(displayRating)) ? '#f59e0b' : '#e2e8f0' }}
              ></i>
            ))}
          </div>
          <div className="rating-text">
            {displayRating}/5
            <span className="reviews-count">{displayReviews > 0 ? ` (${displayReviews} reviews)` : ' (new)'}</span>
          </div>
        </div>

     

        {/* Highlights */}
        {pkg.highlights && pkg.highlights.length > 0 && (
          <div className="highlights">
            {pkg.highlights.slice(0, 3).map((highlight, index) => (
              <span key={index} className="highlight-item">
                <i className="fas fa-check-circle"></i>
                {highlight}
              </span>
            ))}
            {pkg.highlights.length > 3 && (
              <span className="highlight-item more">
                +{pkg.highlights.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Inclusions Preview */}
       

        {/* Footer */}
        <div className="card-footer">
          <div className="price-section">
            <div className="price-wrapper">
              {pkg.originalPrice && (
                <span className="original-price">
                  ₹{typeof pkg.originalPrice === 'number' ? pkg.originalPrice.toLocaleString() : pkg.originalPrice}
                </span>
              )}
              <span className="current-price">
                ₹{typeof pkg.price === 'number' ? pkg.price.toLocaleString() : pkg.price}
              </span>
            </div>
            <span className="price-label">per person</span>
          </div>
          
          <Link to={buildPackageUrl(pkg)} className="view-details-btn">
            <span>View Details</span>
            <i className="fas fa-arrow-right"></i>
          </Link>
        </div>

        {/* Quick Book Button */}
        <Link to={`/booking/${pkg.id}`} className="quick-book-btn">
          <i className="fas fa-bolt"></i>
          <span>Quick Book</span>
        </Link>
      </div>

      {/* Custom CSS */}
      <style>{`
        .package-card {
          position: relative;
          background: white;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.1);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .package-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 25px 50px -12px rgba(102, 126, 234, 0.25);
        }

        /* Card Badges */
        .card-badges {
          position: absolute;
          top: 1rem;
          left: 1rem;
          right: 1rem;
          display: flex;
          gap: 0.5rem;
          z-index: 10;
          pointer-events: none;
          flex-wrap: wrap;
        }

        .badge {
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.025em;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          backdrop-filter: blur(4px);
        }

        .discount-badge {
          color: white;
          animation: pulse 2s infinite;
        }

        .category-badge {
          margin-left: auto;
        }

        .trending-badge {
          background: rgba(10, 90, 165, 0.95);
          backdrop-filter: blur(4px);
          color: white;
        }

        .trending-badge i {
          animation: bounce 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.9; transform: scale(1.05); }
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }

        /* Image Section */
        .card-image-wrapper {
          position: relative;
          padding-top: 66.67%; /* 3:2 Aspect Ratio */
          overflow: hidden;
        }

        .card-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .package-card:hover .card-image {
          transform: scale(1.1);
        }

        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(0, 0, 0, 0.1) 50%,
            rgba(0, 0, 0, 0.3) 100%
          );
        }

        .image-content {
          position: absolute;
          bottom: 1rem;
          right: 1rem;
          z-index: 5;
        }

        .duration-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(4px);
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e293b;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .duration-badge i {
          color: #ec3134;
        }

        /* Content Section */
        .card-content {
          padding: 1.5rem;
          flex: 1;
          display: flex;
          flex-direction: column;
          background: white;
        }

        .package-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 0.5rem;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .destinations {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #475569;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        .destinations i {
          color: #ec3134;
          font-size: 1rem;
        }

        .destinations span {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Rating Section */
        .rating-section {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
          padding: 0.5rem 0;
          border-top: 1px solid #f1f5f9;
          border-bottom: 1px solid #f1f5f9;
        }

        .stars {
          display: flex;
          gap: 0.125rem;
        }

        .stars i {
          font-size: 0.875rem;
        }

        .rating-text {
          font-size: 0.875rem;
          font-weight: 600;
          color: #0f172a;
        }

        .reviews-count {
          font-weight: 400;
          color: #64748b;
        }

        /* Highlights */
        .highlights {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .highlight-item {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.75rem;
          background: #f8fafc;
          border-radius: 50px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #334155;
        }

        .highlight-item i {
          color: #22c55e;
          font-size: 0.75rem;
        }

        .highlight-item.more {
          background: #f1f5f9;
          color: #475569;
        }

        /* Inclusions Preview */
        .inclusions-preview {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 12px;
          justify-content: space-around;
        }

        .inclusion-icon {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
          color: #64748b;
          font-size: 0.75rem;
          transition: all 0.3s;
          cursor: help;
        }

        .inclusion-icon i {
          font-size: 1.25rem;
          color: #ec3134;
          transition: all 0.3s;
        }

        .inclusion-icon:hover i {
          transform: translateY(-2px);
          color: #ea580c;
        }

        /* Footer */
        .card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: auto;
          padding-top: 1rem;
          border-top: 1px solid #f1f5f9;
        }

        .price-section {
          display: flex;
          flex-direction: column;
        }

        .price-wrapper {
          display: flex;
          align-items: baseline;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .original-price {
          font-size: 0.875rem;
          color: #94a3b8;
          text-decoration: line-through;
        }

        .current-price {
          font-size: 16px;
          font-weight: 700;
          color: #ec3134;
          line-height: 1.2;
        }

        .price-label {
          font-size: 0.75rem;
          color: #64748b;
        }

        .view-details-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1.25rem;
          background: linear-gradient(135deg, #ec3134, #ea580c);
          color: white;
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s;
          box-shadow: 0 4px 6px -1px rgba(249, 115, 22, 0.2);
        }

        .view-details-btn:hover {
          transform: translateX(4px);
          box-shadow: 0 10px 15px -3px rgba(249, 115, 22, 0.3);
        }

        .view-details-btn i {
          transition: transform 0.3s;
        }

        .view-details-btn:hover i {
          transform: translateX(4px);
        }

        /* Quick Book Button - Mobile */
        .quick-book-btn {
          display: none;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1rem;
          padding: 0.75rem;
          background: linear-gradient(135deg, #ec3134, #ea580c);
          color: white;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s;
        }

        .quick-book-btn i {
          font-size: 1.25rem;
        }

        .quick-book-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px -5px rgba(249, 115, 22, 0.4);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .view-details-btn {
            display: flex;
          }

          .quick-book-btn {
            display: none;
          }

          .card-footer {
            padding-top: 0.75rem;
          }

          .current-price {
            font-size: 1.25rem;
          }
        }

        @media (max-width: 480px) {
          .card-content {
            padding: 1rem;
          }

          .package-title {
            font-size: 1.125rem;
          }

          .inclusions-preview {
            gap: 0.75rem;
          }

          .inclusion-icon i {
            font-size: 1rem;
          }

          .badge {
            padding: 0.375rem 0.75rem;
            font-size: 0.75rem;
          }
        }

        /* Dark Mode Support (Optional) */
        @media (prefers-color-scheme: dark) {
          .package-card {
            background: #1e293b;
          }

          .package-title {
            color: #f1f5f9;
          }

          .destinations {
            color: #cbd5e1;
          }

          .rating-section {
            border-color: #334155;
          }

          .rating-text {
            color: #f1f5f9;
          }

          .reviews-count {
            color: #94a3b8;
          }

          .highlight-item {
            background: #334155;
            color: #cbd5e1;
          }

          .inclusions-preview {
            background: #0f172a;
          }

          .inclusion-icon {
            color: #94a3b8;
          }

          .card-footer {
            border-color: #334155;
          }

          .original-price {
            color: #64748b;
          }
        }
      `}</style>
    </div>
  );
};

export default PackageCard;
